import { useState, useCallback, useEffect } from 'react'
import type { Adventure, AdventureState, Choice, TillyExpression } from '../types'
import { getRandomReaction, tillyCorrectReactions, tillyWrongReactions } from '../data/adventures/countingApples'

interface UseAdventureReturn {
  currentState: AdventureState
  currentSegment: any
  progress: number
  choiceHistory: Choice[]
  quizResults: boolean[]
  currentQuizIndex: number // For challenge sequences
  tillyLine: string
  tillyExpression: TillyExpression
  isTillySpeaking: boolean
  handleChoice: (choice: Choice) => void
  handleQuizComplete: (correct: boolean) => void
  handleNext: () => void
  canGoNext: boolean
  isComplete: boolean
  // For challenge mode
  challengeQuizIndex: number
  totalChallengeQuizzes: number
  isChallengeMode: boolean
}

// State machine transition map - deterministic flow
const STATE_TRANSITIONS: Record<AdventureState, AdventureState> = {
  'intro': 'story-1',
  'story-1': 'choice-1',
  'choice-1': 'quiz-1',
  'quiz-1': 'story-2',
  'story-2': 'choice-2',
  'choice-2': 'quiz-2',
  'quiz-2': 'challenge',
  'challenge': 'quiz-sequence',
  'quiz-sequence': 'reward',
  'reward': 'complete',
  'complete': 'complete'
}

// Ordered state progression for progress calculation
const STATE_ORDER: AdventureState[] = [
  'intro',
  'story-1',
  'choice-1',
  'quiz-1',
  'story-2',
  'choice-2',
  'quiz-2',
  'challenge',
  'quiz-sequence',
  'reward',
  'complete'
]

export function useAdventure(adventure: Adventure): UseAdventureReturn {
  const [currentState, setCurrentState] = useState<AdventureState>('intro')
  const [choiceHistory, setChoiceHistory] = useState<Choice[]>([])
  const [quizResults, setQuizResults] = useState<boolean[]>([])
  const [challengeQuizIndex, setChallengeQuizIndex] = useState(0)
  
  // Tilly state
  const [tillyLine, setTillyLine] = useState(adventure.segments.intro.tillyLine)
  const [tillyExpression, setTillyExpression] = useState<TillyExpression>('happy')
  const [isTillySpeaking, setIsTillySpeaking] = useState(true)

  const getCurrentSegment = useCallback(() => {
    switch (currentState) {
      case 'intro':
        return adventure.segments.intro
      case 'story-1':
        return adventure.segments.story1
      case 'choice-1':
        return adventure.segments.choice1
      case 'quiz-1':
        return adventure.segments.quiz1
      case 'story-2':
        return adventure.segments.story2
      case 'choice-2':
        return adventure.segments.choice2
      case 'quiz-2':
        return adventure.segments.quiz2
      case 'challenge':
        return adventure.segments.challenge.intro
      case 'quiz-sequence':
        return adventure.segments.challenge.quizzes[challengeQuizIndex]
      case 'reward':
        return adventure.segments.reward
      case 'complete':
        return null
      default:
        return null
    }
  }, [currentState, adventure, challengeQuizIndex])

  const calculateProgress = useCallback(() => {
    const currentIndex = STATE_ORDER.indexOf(currentState)
    // Adjust for challenge progress
    if (currentState === 'quiz-sequence') {
      const challengeProgress = challengeQuizIndex / adventure.segments.challenge.quizzes.length
      const baseProgress = currentIndex / (STATE_ORDER.length - 1)
      return Math.round((baseProgress + (challengeProgress * 0.1)) * 100)
    }
    return Math.round((currentIndex / (STATE_ORDER.length - 1)) * 100)
  }, [currentState, challengeQuizIndex, adventure])

  // Auto-set Tilly's line when state changes
  useEffect(() => {
    const segment = getCurrentSegment()
    if (!segment) return

    let newLine = ''
    let newExpression: TillyExpression = 'happy'

    switch (currentState) {
      case 'intro':
      case 'story-1':
      case 'story-2':
      case 'challenge': {
        const storySegment = segment as { tillyLine: string }
        newLine = storySegment.tillyLine
        newExpression = 'happy'
        break
      }
      case 'choice-1':
      case 'choice-2': {
        // Show first choice's Tilly line as prompt
        const choices = segment as Choice[]
        newLine = choices[0]?.tillyLine || "What should we do?"
        newExpression = 'thinking'
        break
      }
      case 'quiz-1':
      case 'quiz-2':
      case 'quiz-sequence': {
        const quiz = segment as { tillyPrompt: string }
        newLine = quiz?.tillyPrompt || "Can you solve this?"
        newExpression = 'thinking'
        break
      }
      case 'reward': {
        const reward = segment as { tillyLine: string }
        newLine = reward.tillyLine
        newExpression = 'celebrating'
        break
      }
    }

    setTillyLine(newLine)
    setTillyExpression(newExpression)
    setIsTillySpeaking(true)
  }, [currentState, getCurrentSegment])

  const handleChoice = useCallback((choice: Choice) => {
    setChoiceHistory(prev => [...prev, choice])
    setTillyLine(choice.tillyLine)
    setTillyExpression('encouraging')
    setIsTillySpeaking(true)
    
    // Small delay before advancing so Tilly can "speak"
    setTimeout(() => {
      setCurrentState(choice.nextState)
    }, 1500)
  }, [])

  const handleQuizComplete = useCallback((correct: boolean) => {
    setQuizResults(prev => [...prev, correct])
    
    // Tilly reacts based on answer
    if (correct) {
      const reaction = getRandomReaction(tillyCorrectReactions)
      setTillyLine(reaction)
      setTillyExpression('celebrating')
    } else {
      const reaction = getRandomReaction(tillyWrongReactions)
      setTillyLine(reaction)
      setTillyExpression('encouraging')
    }
    setIsTillySpeaking(true)
    
    // Delay before advancing
    setTimeout(() => {
      if (currentState === 'quiz-sequence') {
        // Check if there are more challenge quizzes
        const nextIndex = challengeQuizIndex + 1
        if (nextIndex < adventure.segments.challenge.quizzes.length) {
          setChallengeQuizIndex(nextIndex)
        } else {
          // Challenge complete, move to reward
          setCurrentState('reward')
        }
      } else {
        // Regular quiz flow
        const next = STATE_TRANSITIONS[currentState]
        if (next && next !== currentState) {
          setCurrentState(next)
        }
      }
    }, 2500)
  }, [currentState, challengeQuizIndex, adventure])

  const handleNext = useCallback(() => {
    const currentIndex = STATE_ORDER.indexOf(currentState)
    if (currentIndex < STATE_ORDER.length - 1) {
      setCurrentState(STATE_ORDER[currentIndex + 1])
    }
  }, [currentState])

  const currentSegment = getCurrentSegment()
  const isChallengeMode = currentState === 'quiz-sequence'

  return {
    currentState,
    currentSegment,
    progress: calculateProgress(),
    choiceHistory,
    quizResults,
    currentQuizIndex: challengeQuizIndex,
    tillyLine,
    tillyExpression,
    isTillySpeaking,
    handleChoice,
    handleQuizComplete,
    handleNext,
    canGoNext: currentState !== 'complete' && currentState !== 'quiz-1' && currentState !== 'quiz-2' && currentState !== 'quiz-sequence',
    isComplete: currentState === 'complete',
    challengeQuizIndex,
    totalChallengeQuizzes: adventure.segments.challenge.quizzes.length,
    isChallengeMode
  }
}
